<template>
  <div class="exploration-table fd-table mt-2">
    <div class="table-top-content mb-2">
      <div class="fd-dm-table-heading">
        <div class="fd-dm-table-heading__row">
          <p class="recent-activity-wrap">Fluids Characterization Data</p>
          <div class="test-status-legend" v-if="filterData?.samples?.length > 0">
            <span class="test-status-legend__item"><i class="test-status-legend__dot test-status-legend__dot--passed"></i>Passed</span>
            <span class="test-status-legend__item"><i class="test-status-legend__dot test-status-legend__dot--failed"></i>Failed</span>
            <span class="test-status-legend__item"><i class="test-status-legend__dot test-status-legend__dot--pending"></i>Pending</span>
          </div>
        </div>
        <div class="fd-dm-filter-bar" v-if="appliedFilterChips.length || filterData?.samples?.length > 0">
          <div class="fd-dm-filter-bar__actions" >
           <div class="fd-dm-filter-chips">
            <div
              v-for="chip in appliedFilterChips"
              :key="chip.id"
              class="fd-dm-filter-chip"
            >
              <span class="fd-dm-filter-chip__text">
                <span class="fd-dm-filter-chip__label">{{ chip.label }}</span>
                <span class="fd-dm-filter-chip__value">{{ chip.value }}</span>
              </span>
              <button
                type="button"
                class="fd-dm-filter-chip__remove"
                @click="removeFilterChip(chip)"
              >
                <img :src="close_icon" alt="" width="14" height="14" class="fd-action-icon" />
              </button>
            </div>
            </div>
            <button
              v-if="filterData?.samples?.length > 0"
              type="button"
              class="fd-dm-reset-filters-btn"
              title="Reset filters"
              @click="resetAllFilters"
            >
              <img :src="resetIcon" alt="reset" width="16" height="16" />
            </button>
            <button
            v-if="filterData?.samples?.length > 0"
              type="button"
              class="fd-dm-more-filters-btn"
              @click="openMoreFilters"
            >
              <v-icon size="16" icon="mdi-filter-variant" />
              More filters
            </button>
            <div class="pdf-btn fd-dm-csv-btn" title="Exporting" v-if="loadingCsv && filterData?.samples?.length > 0">
              Exporting..
            </div>
            <div v-else class="pdf-btn fd-dm-csv-btn" @click="downloadTableCSV" title="Export CSV" v-if="filterData?.samples?.length > 0">
              <img :src="csvIcon" alt="csv" width="24" height="24" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <v-data-table-server
      :page="page"
      :items-per-page="itemsPerPage"
      @update:page="emitPaginationPage"
      @update:items-per-page="emitPaginationItemsPerPage"
      :headers="headers"
      :items="tableItems"
      :items-per-page-options="pageOptions"
      :items-length="loading ? tableItems.length : totalItems"
      :loading="false"
      item-value="ID"
      class="fd-dm-char-data-table"
    >
      <template v-slot:headers>
        <tr>
          <th v-for="header in headers" :key="header.value" class="text-left search-dropdown-menu"
            :class="{
              'test-column': header.key === 'tests',
              'pvt-column': header.key === 'pvtTests',
              'icon-column': header.key === 'report' || header.key === 'exportPVTSim',
              'is-filter-open': searchDropdowns[header.key],
            }">

            <div class="d-flex align-center justify-space-between fd-dm-th-inner">
              <div class="sort-icon-title d-flex align-center fd-dm-sort-title">
                {{ header.title }}
              </div>
              <v-menu
                v-if="header.key != 'report' && header.key != 'exportPVTSim' && header.key != 'pvtTests'"
                v-model="searchDropdowns[header.key]"
                :close-on-content-click="false"
                location="bottom end"
                offset="6"
              >
                <template v-slot:activator="{ props }">
                  <div
                    class="search-icon"
                    v-bind="props"
                    :id="`menu-activator-${header.key}`"
                    @click="showSearchDropdown(header.key)"
                  >
                    <v-img :src="searchDropdowns[header.key] ? close_icon : magnify" alt=""></v-img>
                  </div>
                </template>
                <v-list class="fd-dm-column-filter-list">
                  <v-text-field variant="solo" class="search-table" v-model="searchQuery[header.key]"
                    :label="`Search ${header.title}`" single-line hide-details>
                    <template v-slot:prepend>
                      <v-img :src="magnify_black" class="ml-2" width="20" height="20"></v-img>
                    </template>
                  </v-text-field>
                  <v-skeleton-loader v-if="searchLoading" class="pa-2" />
                  <v-list-item v-else-if="filteredItems(header.key).length === 0" disabled>
                    <v-list-item-title class="fd-dm-column-filter-empty">No data found</v-list-item-title>
                  </v-list-item>
                  <v-list-item class="list-items fd-dm-column-filter-item"
                    v-for="location in filteredItems(header.key)" :key="location.name">
                    <div class="data-list-content">
                      <input variant="solo" class="custom-field" type="checkbox" :model-value="location.isSelected"
                        :checked="location.isSelected"
                        @change="toggleSelection(header.key, location.name, location.isSelected, location.key)" @click.stop />
                      <span>{{ location.name }}</span>
                    </div>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </th>
        </tr>
      </template>
      <template v-slot:item="{ item }">
        <tr v-if="item.__skeleton" class="fd-dm-char-skeleton-row">
          <td
            v-for="header in headers"
            :key="header.key"
            :class="{
              'test-column': header.key === 'tests',
              'pvt-column': header.key === 'pvtTests',
              'icon-column': header.key === 'report' || header.key === 'exportPVTSim',
            }"
          >
            <span class="fd-table-skeleton-cell" />
          </td>
        </tr>
        <tr v-else>
          <td>
            <template v-if="item.fluidSampleID">
              <span class="clickable-cell no-bg" @click="navigateToSampleInformation(item.ID)">{{
                item.fluidSampleID
              }}</span>
            </template>
          </td>
          <td>
            <template v-if="item.location?.asset">
              <span class="clickable-cell no-bg" @click="navigateToAsset(item.location.asset, item.ID)">{{
                item.location.asset
              }}</span>
            </template>
            <template v-else>–</template>
          </td>
          <td>
            <template v-if="item.location?.field">
              <span class="clickable-cell no-bg"
                @click="navigateToField(item.location.asset, item.location.field, item.ID)">{{ item.location.field
                }}</span>
            </template>
            <template v-else>–</template>
          </td>
          <td>
            <template v-if="item.location?.well">
              <span class="clickable-cell no-bg"
                @click="navigateToWell(item.location.asset, item.location.field, item.location.well, item.ID)">{{
                  item.location.well }}</span>
            </template>
            <template v-else>–</template>
          </td>
          <td>{{ item.location?.reservoir || '–' }}</td>
          <td>{{ item.fluidType || '–' }}</td>
          <td class="test-column">
            <div class="test-links">
              <span :class="getTestStatusClass({ type: 'Sample Validation' }, item)" @click="navigateToSampleValidation(item.location.asset, item.location.field, item.location.well, item.ID)">
                Sample Validation
              </span>
              <span v-if="item.compositiontests?.length" :class="getTestStatusClass(test, item)"
                @click="navigateToTest(item.location.asset, item.location.field, item.location.well, 'compositiontests', test.ID, item.ID)" v-for="(test, index) in item.compositiontests" :key="index">
                <template v-if="test.contaminationType || test.recombinationType">{{ `Composition (${test.contaminationType || test.recombinationType}${index < item.compositiontests.length - 1 ? ' /' : ''})` }}</template>
                <template v-else>Composition</template>
              </span>
              <template v-if="item.IsTestAvailable">
                <template v-if="item.ccetests?.length">
                  <span v-for="(test, index) in item.ccetests" :key="index" :class="getTestStatusClass(test, item)"
                    @click="navigateToTest(item.location.asset, item.location.field, item.location.well, 'ccetest', test.ID, item.ID)">
                    {{ test.name || 'CCE' }}{{ item.ccetests.length > 1 ? ` (${test.testTemperature ? `${test.testTemperature} ${test.testTemperature_UOM}` : test.testNumber})` : '' }}
                  </span>
                </template>
                <template v-if="item.dltests?.length">
                  <span v-for="(test, index) in item.dltests" :key="index" :class="getTestStatusClass(test, item)"
                    @click="navigateToTest(item.location.asset, item.location.field, item.location.well, 'dltest', test.ID, item.ID)">
                    {{ test.name || 'DL' }}{{ item.dltests.length > 1 ? ` (${test.testTemperature ? `${test.testTemperature} ${test.testTemperature_UOM}` : test.testNumber})` : '' }}
                  </span>
                </template>
                <template v-if="item.cvdtests?.length">
                  <span v-for="(test, index) in item.cvdtests" :key="index" :class="getTestStatusClass(test, item)"
                    @click="navigateToTest(item.location.asset, item.location.field, item.location.well, 'cvdtest', test.ID, item.ID)">
                    {{ test.name || 'CVD' }}{{ item.cvdtests.length > 1 ? ` (${test.testTemperature ? `${test.testTemperature} ${test.testTemperature_UOM}` : test.testNumber})` : '' }}
                  </span>
                </template>
                <template v-if="item.separatortests?.length">
                  <span v-for="(test, index) in item.separatortests" :key="index" :class="getTestStatusClass(test, item)"
                    @click="navigateToTest(item.location.asset, item.location.field, item.location.well, 'separatortest', test.ID, item.ID)">
                    {{ test.name || 'Separator Test' }}{{ item.separatortests.length > 1 ? ` (${test.testTemperature ? `${test.testTemperature} ${test.testTemperature_UOM}` : test.testNumber})` : '' }}
                  </span>
                </template>
                <template v-if="item.viscositytests?.length">
                  <span v-for="(test, index) in item.viscositytests" :key="index" :class="getTestStatusClass(test, item)"
                    @click="navigateToTest(item.location.asset, item.location.field, item.location.well, 'viscositytest', test.ID, item.ID)">
                    {{ test.name || 'Viscosity' }}{{ item.viscositytests.length > 1 ? ` (${test.testTemperature ? `${test.testTemperature} ${test.testTemperature_UOM}` : test.testNumber})` : '' }}
                  </span>
                </template>
              </template>
            </div>
          </td>
          <td class="pvt-column">
            <div class="test-links">
              <template v-if="item.pvt?.IsTestAvailable || item.pvt?.isTestAvailable">
                <template v-if="item.pvt.ccetests?.length">
                  <span v-for="(test, index) in item.pvt.ccetests" :key="`pvt-cce-${index}`" class="pvt-predicted-test mr-0"
                    @click="navigateToTest(null, null, null, 'ccetest', null, null, { item, testArrayKey: 'ccetests', test, testIndex: index, resolvePvt: true })">
                    {{ test.name || 'CCE' }}{{ item.pvt.ccetests.length > 1 ? ` (${test.testTemperature ? `${test.testTemperature} ${test.testTemperature_UOM}` : test.testNumber})` : '' }}
                  </span>
                </template>
                <template v-if="item.pvt.dltests?.length">
                  <span v-for="(test, index) in item.pvt.dltests" :key="`pvt-dl-${index}`" class="pvt-predicted-test mr-0"
                    @click="navigateToTest(null, null, null, 'dltest', null, null, { item, testArrayKey: 'dltests', test, testIndex: index, resolvePvt: true })">
                    {{ test.name || 'DL' }}{{ item.pvt.dltests.length > 1 ? ` (${test.testTemperature ? `${test.testTemperature} ${test.testTemperature_UOM}` : test.testNumber})` : '' }}
                  </span>
                </template>
                <template v-if="item.pvt.cvdtests?.length">
                  <span v-for="(test, index) in item.pvt.cvdtests" :key="`pvt-cvd-${index}`" class="pvt-predicted-test mr-0"
                    @click="navigateToTest(null, null, null, 'cvdtest', null, null, { item, testArrayKey: 'cvdtests', test, testIndex: index, resolvePvt: true })">
                    {{ test.name || 'CVD' }}{{ item.pvt.cvdtests.length > 1 ? ` (${test.testTemperature ? `${test.testTemperature} ${test.testTemperature_UOM}` : test.testNumber})` : '' }}
                  </span>
                </template>
                <template v-if="item.pvt.separatortests?.length">
                  <span v-for="(test, index) in item.pvt.separatortests" :key="`pvt-separator-${index}`" class="pvt-predicted-test mr-0"
                    @click="navigateToTest(null, null, null, 'separatortest', null, null, { item, testArrayKey: 'separatortests', test, testIndex: index, resolvePvt: true })">
                    {{ test.name || 'Separator Test' }}{{ item.pvt.separatortests.length > 1 ? ` (${test.testTemperature ? `${test.testTemperature} ${test.testTemperature_UOM}` : test.testNumber})` : '' }}
                  </span>
                </template>
                <template v-if="item.pvt.viscositytests?.length">
                  <span v-for="(test, index) in item.pvt.viscositytests" :key="`pvt-viscosity-${index}`" class="pvt-predicted-test mr-0"
                    @click="navigateToTest(null, null, null, 'viscositytest', null, null, { item, testArrayKey: 'viscositytests', test, testIndex: index, resolvePvt: true })">
                    {{ test.name || 'Viscosity' }}{{ item.pvt.viscositytests.length > 1 ? ` (${test.testTemperature ? `${test.testTemperature} ${test.testTemperature_UOM}` : test.testNumber})` : '' }}
                  </span>
                </template>
              </template>
            </div>
          </td>
          <td class="icon-column flex justify-center items-center">
            <img v-if="sourceDownloadId === item.ID"
                 :src="reportLoader"
                 alt="Loading"
                 class="source-report-icon source-report-icon--loading" />
            <img v-else-if="hasPvtPredictedTests(item)"
                 @click="downloadPvtInput(item)"
                 :src="excelIcon"
                 alt="Excel"
                 class="source-report-icon" />
            <img v-else-if="item.source && item.source != 'Synthetic Data'" 
                 @click="goToPdf(item.ID, !item.source.includes('.') ? 'pdf' :
                   item.source.toLowerCase().endsWith('.pdf') ? 'pdf' : 
                   item.source.toLowerCase().endsWith('.xlsm') ? 'xlsm' :
                   item.source.toLowerCase().endsWith('.xlsx') ? 'xlsx' :
                   item.source.toLowerCase().endsWith('.xls') ? 'xls' : 'pdf')" 
                 :src="!item.source.includes('.') ? pdfIcon :
                   item.source.toLowerCase().endsWith('.pdf') ? pdfIcon : 
                   item.source.toLowerCase().endsWith('.xlsm') ? excelIcon :
                   item.source.toLowerCase().endsWith('.xlsx') ? excelIcon :
                   item.source.toLowerCase().endsWith('.xls') ? excelIcon : pdfIcon"
                 :alt="!item.source.includes('.') ? 'PDF' :
                   item.source.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Excel'"
                 class="source-report-icon" />
          </td>
          <td class="icon-column flex justify-center items-center">
            <img v-if="exportDownloadId === item.ID"
                 :src="reportLoader"
                 alt="Loading"
                 class="source-report-icon source-report-icon--loading" />
            <img v-else-if="item.ID && item.fluidSampleID"
                 @click="exportToPVTSim(item.ID, item.fluidSampleID)"
                 :src="excelIcon"
                 alt="Export to PVTSim"
                 class="source-report-icon" />
          </td>

        </tr>
      </template>
      <template v-slot:loader></template>
      <template v-slot:no-data>
        <div v-if="!loading" class="d-flex justify-center align-center pa-4">
          No data available
        </div>
      </template>
      <template #bottom></template>
    </v-data-table-server>
  </div>
</template>

<script>
import api from "../../axiosInterceptor";
import axios from "axios";
import { getTenantPrefix } from "../../utils/tenant"
import { executePvtInputDownload, executeSampleReportDownload } from "../../utils/sampleReportDownload";
import {
  appendRouteQuery,
  buildPvtTestRouteQuery,
  clearPvtTestNavigation,
  getPvtTestListEndpoint,
  getPvtTestsFromPayload,
  markPvtTestNavigation,
} from "../../utils/pvtTestRoute";
import store from "../../store"

export default {
  name: "TableData",
  props: {
    page: { type: Number, default: 1 },
    itemsPerPage: { type: Number, default: 20 },
    filterData: {
      type: Object,
      default: () => ({})
    },
    filterDetails: {
      type: Object,
      default: () => ({})
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    showFilter: {
      type: Boolean,
      default: true,
    }
  },
  data() {
    return {
      sort_up: require("../../assets/Svg/Table/up.svg"),
      sort_down: require("../../assets/Svg/Table/down.svg"),
      magnify: require("../../assets/Svg/Icons/search.svg"),
      magnify_black: require("../../assets/Svg/Icons/search.svg"),
      close_icon: require("../../assets/Svg/Icons/cross-circle.svg"),
      pdfIcon: require("../../assets/Svg/pdfIcon.svg"),
      excelIcon: require("../../assets/Svg/excelIcon.svg"),
      csvIcon: require("../../assets/Svg/Icons/file-csv.svg"),
      resetIcon: require("../../assets/Svg/Icons/reset.svg"),
      reportLoader: require("../../assets/Images/pdfLoader.gif"),
      sourceDownloadId: null,
      exportDownloadId: null,
      headers: [
        {
          title: 'Sample ID',
          sortable: false,
          key: 'fluidSampleID',
        },
        {
          title: 'Assets',
          sortable: false,
          key: 'assets',
        },
        {
          title: 'Fields',
          key: 'fields',
          sortable: false,
        },
        {
          title: 'Wells',
          key: 'wells',
          sortable: false,
        },
        { title: 'Reservoir', key: 'reservoirs', sortable: false, },
        { title: 'Fluid Type', key: 'fluidType', sortable: false, },
        { title: 'Experimental Tests', key: 'tests', sortable: false, },
        { title: 'AI Predicted Tests', key: 'pvtTests', sortable: false, },
        { title: 'Source Reports', key: 'report', sortable: false, },
        { title: 'Export to PVTSim', key: 'exportPVTSim', sortable: false, },
      ],
      serverItems: [],
      loading: true,
      totalItems: 0,
      pageOptions: [20, 50, 100, 200, 300, 400, 500],
      searchDropdowns: {
        assets: false,
        fields: false,
        wells: false,
        reservoirs: false,
      },
      selectedLocations: {
        assets: [],
        fields: [],
        wells: [],
        reservoirs: [],
        fluidTypes: [],
        tests: [],
        fluidSampleID: []
      },
      searchDropdownItems: {
        assets: [],
        fields: [],
        wells: [],
        reservoirs: [],
        fluidType: [],
        tests: [],
        fluidSampleID: []
      },
      searchQuery: [], // New property for search input
      searchLoading: false,
      samplesCSV: [],
      loadingCsv: false
    }
  },
  computed: {
    tableItems() {
      if (this.loading) {
        return Array.from({ length: 8 }, (_, i) => ({
          ID: `__skeleton-${i}`,
          __skeleton: true,
        }));
      }
      return this.serverItems;
    },
    appliedFilterChips() {
      const details = this.filterDetails;
      if (!details || typeof details !== "object") return [];
      const fields = [
        { key: "fluidSampleID", label: "Sample ID" },
        { key: "asset", label: "Asset" },
        { key: "field", label: "Field" },
        { key: "well", label: "Well" },
        { key: "reservoir", label: "Reservoir" },
        { key: "fluidType", label: "Fluid Type" },
        { key: "sampleType", label: "Sample Type" },
        { key: "fluidClassification", label: "Fluid Classification" },
        { key: "selectedTests", label: "Test" },
      ];
      const chips = [];
      fields.forEach(({ key, label }) => {
        const values = details[key];
        if (!Array.isArray(values)) return;
        values.forEach((value) => {
          if (value === undefined || value === null || value === "") return;
          chips.push({
            id: `${key}-${value}`,
            key,
            label,
            value: String(value),
          });
        });
      });
      return chips;
    },
    filteredItems() {
      return (key) => {
        const items = this.searchDropdownItems[key] || [];
        const searchQuery = this.searchQuery[key]?.toLowerCase() || '';
        const selectedLocations = this.selectedLocations[key] || [];

        const matchingItems = items.filter((item) =>
          item.name?.toLowerCase().includes(searchQuery)
        );
        const itemsToShow = searchQuery
          ? matchingItems // Show only matching items when there's a search query
          : items; // Show all items when no search query exists

        
        const result = itemsToShow.map((item) => {
          const isCompositionSelected = item.name === 'Composition' && selectedLocations.includes('compositiontests');
          return {
            ...item,
            isSelected: item.isSelected || selectedLocations.includes(item.name) || isCompositionSelected,
          };
        });

        return result;

      };
    },
  },
  methods: {
    removeFilterChip(chip) {
      const next = { ...(this.filterDetails || {}) };
      const list = Array.isArray(next[chip.key]) ? [...next[chip.key]] : [];
      next[chip.key] = list.filter((value) => String(value) !== String(chip.value));
      this.$emit("getFilterFromTable", {
        ...next,
        withFilter: true,
        page: 1,
        itemsPerPage: this.itemsPerPage,
      });
    },
    toggleSelection(column, locationName, isSelected, testKey) {
      let target = locationName;
      if (testKey === 'compositiontests') {
        target = testKey;
      }

      const selectedLocations = this.selectedLocations[column] || [];

      this.searchDropdownItems[column].forEach(item => {
        if (item.isSelected && !selectedLocations.includes(item.name)) {
          selectedLocations.push(item.name);
        }
      });

      const index = selectedLocations.indexOf(target);

      if (isSelected) {
        selectedLocations.splice(index, 1);
      } else {
        selectedLocations.push(target);
      }

      this.selectedLocations[column] = selectedLocations;

      this.searchDropdownItems[column].forEach(item => {
        if (item.name === target) {
          item.isSelected = !isSelected;  // Toggle the selection state
        }
      });

      const columnMapping = {
        assets: "asset",
        fields: "field",
        wells: "well",
        reservoirs: "reservoir",
        fluidType: "fluidType",
        tests: "selectedTests",
        fluidSampleID: "fluidSampleID",
      };

      const filterKey = columnMapping[column];
      if (!filterKey) {
        return;
      }

      if (this.filterDetails) {
        if (isSelected) {
          if (this.filterDetails[filterKey]) {
            const filterIndex = this.filterDetails[filterKey].indexOf(target);
            if (filterIndex !== -1) {
              this.filterDetails[filterKey].splice(filterIndex, 1);
            }
          }
        } else {
          if (!this.filterDetails[filterKey]) {
            this.filterDetails[filterKey] = [];
          }

          if (!this.filterDetails[filterKey].includes(target)) {
            this.filterDetails[filterKey].push(target);
          }
        }

      }
      this.selectLocation();
    },
    emitPaginationPage(page) {
      this.$emit('update:pagination', {
        page: Number(page),
        itemsPerPage: Number(this.itemsPerPage),
      });
      this.loadItems();
      localStorage.setItem('itemsPerPage', String(this.itemsPerPage));
    },
    emitPaginationItemsPerPage(itemsPerPage) {
      const n = Number(itemsPerPage);
      this.$emit('update:pagination', { page: 1, itemsPerPage: n });
      this.loadItems();
      localStorage.setItem('itemsPerPage', String(n));
    },

    loadItems() {
      if (this.isLoading) {
        this.loading = true;
        return;
      }
      if (this.filterData && this.filterData.samples) {
        const samples = this.filterData.samples;
        const pagination = this.filterData.pagination;

        this.serverItems = samples;
        this.totalItems = pagination.TotalRecords;
        this.loading = false;
      }else{
        this.serverItems = [];
        this.totalItems = 0;
        this.loading = false;
      }
    },

    navigateToAsset(asset, sampleId) {
      const tenant = getTenantPrefix();
      if (tenant && asset) {
        const encodedAsset = encodeURIComponent(asset).replace(/'/g, "%27").replace(/ /g, "-");
        const url = `/${tenant}/assets/${encodedAsset}`;
        this.$router.push(url);
        localStorage.setItem("lastView", url);
      }
    },

    navigateToField(asset, field, sampleId) {
      const tenant = getTenantPrefix();
      if (tenant && asset && field) {
        const encodedAsset = encodeURIComponent(asset).replace(/'/g, "%27").replace(/ /g, "-");
        const encodedField = encodeURIComponent(field).replace(/'/g, "%27").replace(/ /g, "-");
        const url = `/${tenant}/assets/${encodedAsset}/${encodedField}`;
        this.$router.push(url);
        localStorage.setItem("lastView", url);
      }
    },

    navigateToWell(asset, field, well, sampleId) {
      const tenant = getTenantPrefix();
      if (tenant && asset && field && well) {
        const encodedAsset = encodeURIComponent(asset).replace(/'/g, "%27").replace(/ /g, "-");
        const encodedField = encodeURIComponent(field).replace(/'/g, "%27").replace(/ /g, "-");
        const encodedWell = encodeURIComponent(well).replace(/'/g, "%27").replace(/ /g, "-");
        const url = `/${tenant}/assets/${encodedAsset}/${encodedField}/${encodedWell}`;
        this.$router.push(url);
        localStorage.setItem("lastView", url);
      }
    },

    navigateToSamples(asset, field, well, sampleId) {
      const tenant = getTenantPrefix();
      if (tenant && asset && field && well) {
        const encodedAsset = encodeURIComponent(asset).replace(/'/g, "%27").replace(/ /g, "-");
        const encodedField = encodeURIComponent(field).replace(/'/g, "%27").replace(/ /g, "-");
        const encodedWell = encodeURIComponent(well).replace(/'/g, "%27").replace(/ /g, "-");
        const url = `/${tenant}/assets/${encodedAsset}/${encodedField}/${encodedWell}`;
        this.$router.push(url);
        localStorage.setItem("lastView", url);
      }
    },

    openMoreFilters() {
      const toggle = document.querySelector(".fd-dm .filters-main-div .toggle-icon");
      if (toggle) toggle.click();
    },
    resetAllFilters() {
      this.loading = true;
      this.serverItems = [];
      this.totalItems = 0;
      this.$emit("resetFiltersEmitted");
    },

    getTestId(test) {
      return test?.ID || test?._id || test?.id || test?.testId || null;
    },
    getTestStatusClass(test, item) {
      const sample = item?.originalSample || item;
      if (!sample) {
        return 'test-status-pending pvt-predicted-test mr-0';
      }

      if (test.type === 'Sample Validation') {
        if (sample.IsTestAvailable) {
          return 'test-status-passed pvt-predicted-test mr-0';
        }
        return 'test-status-pending pvt-predicted-test mr-0';
      }

      const testData = test.testData || test;
      if (testData && Object.prototype.hasOwnProperty.call(testData, 'IsVerified')) {
        const isVerifiedValue = testData.IsVerified;
        if (isVerifiedValue === true || isVerifiedValue === 1 || isVerifiedValue === '1') {
          return 'test-status-passed pvt-predicted-test mr-0';
        }
        if (isVerifiedValue === false || isVerifiedValue === 0 || isVerifiedValue === '0') {
          return 'test-status-failed pvt-predicted-test mr-0';
        }
        return 'test-status-pending pvt-predicted-test mr-0';
      }

      return 'test-status-pending pvt-predicted-test mr-0';
    },
    getPvtSampleId(item) {
      const pvt = item.pvt;
      if (!pvt) {
        return item.ID;
      }
      return pvt.ID || pvt._id || pvt.id || item.ID;
    },
    hasPvtPredictedTests(item) {
      const pvt = item?.pvt;
      if (!pvt || !(pvt.IsTestAvailable || pvt.isTestAvailable)) {
        return false;
      }
      return Boolean(
        pvt.ccetests?.length ||
        pvt.dltests?.length ||
        pvt.cvdtests?.length ||
        pvt.separatortests?.length ||
        pvt.viscositytests?.length
      );
    },
    async resolveTestId(sampleId, testArrayKey, test, testIndex, usePvtListEndpoint = false) {
      const directId = this.getTestId(test);
      if (directId) {
        return directId;
      }

      try {
        const endpoint = usePvtListEndpoint
          ? getPvtTestListEndpoint(sampleId, testArrayKey)
          : `public/api/samples/${sampleId}/tests`;
        const response = await api.get(endpoint);
        if (!(response?.data?.statusCode === 200 || response?.data?.statusCode === 201)) {
          return null;
        }

        const data = response.data.data || {};
        const tests = usePvtListEndpoint
          ? getPvtTestsFromPayload(data, testArrayKey)
          : (data?.[testArrayKey] || []);
        if (!tests.length) {
          return null;
        }

        if (test?.testNumber != null) {
          const byNumber = tests.find((entry) => entry.testNumber === test.testNumber);
          if (byNumber) {
            return this.getTestId(byNumber);
          }
        }

        if (test?.testTemperature != null) {
          const byTemperature = tests.find((entry) => entry.testTemperature === test.testTemperature);
          if (byTemperature) {
            return this.getTestId(byTemperature);
          }
        }

        if (testIndex != null && tests[testIndex]) {
          return this.getTestId(tests[testIndex]);
        }

        return this.getTestId(tests[0]);
      } catch (error) {
        console.error("Error resolving AI Predicted test ID:", error);
        return null;
      }
    },
    async navigateToTest(asset, field, well, testType, testId, sampleId, pvtOptions = null) {
      if (pvtOptions?.resolvePvt) {
        const item = pvtOptions.item;
        sampleId = item?.ID;
        testId = await this.resolveTestId(
          sampleId,
          pvtOptions.testArrayKey,
          pvtOptions.test,
          pvtOptions.testIndex,
          true
        );
        const location = item?.location || {};
        asset = location.asset;
        field = location.field;
        well = location.well;

        if (!testId) {
          store.commit("api/showSnackbar", {
            message: "Unable to open AI Predicted test. Test ID not found.",
            color: "#E53535",
            timeout: 5000,
          });
          return;
        }
      }

      const tenant = getTenantPrefix();
      const encodeSeg = (v) => (v ? encodeURIComponent(v).replace(/'/g, "%27").replace(/ /g, "-") : "");
      const pathWithOptionalSample = (encodedAsset, encodedField, encodedWell) => {
        const base = `/${tenant}/assets/${encodedAsset}/${encodedField}/${encodedWell}`;
        return sampleId ? `${base}/${encodeSeg(sampleId)}/${testType}/${testId}` : `${base}/${testType}/${testId}`;
      };
      const pushRoute = (url) => {
        let target = url;
        if (pvtOptions?.resolvePvt) {
          target = appendRouteQuery(url, buildPvtTestRouteQuery(pvtOptions.item?.ID));
          markPvtTestNavigation(sampleId);
        } else {
          clearPvtTestNavigation();
        }
        this.$router.push(target);
        localStorage.setItem("lastView", target);
      };

      if (tenant && asset && field && well && testType && testId) {
        const encodedAsset = encodeSeg(asset);
        const encodedField = encodeSeg(field);
        const encodedWell = encodeSeg(well);
        pushRoute(pathWithOptionalSample(encodedAsset, encodedField, encodedWell));
      } else {
        const notAvailable = "NotAvail";
        const encodedAsset = asset ? encodeSeg(asset) : notAvailable;
        const encodedField = field ? encodeSeg(field) : notAvailable;
        const encodedWell = well ? encodeSeg(well) : notAvailable;
        pushRoute(pathWithOptionalSample(encodedAsset, encodedField, encodedWell));
      }
    },
    navigateToSampleValidation(asset, field, well, sampleId) {
      const tenant = getTenantPrefix();
      if (tenant && asset && field && well && sampleId) {
        const encodedAsset = encodeURIComponent(asset).replace(/'/g, "%27").replace(/ /g, "-");
        const encodedField = encodeURIComponent(field).replace(/'/g, "%27").replace(/ /g, "-");
        const encodedWell = encodeURIComponent(well).replace(/'/g, "%27").replace(/ /g, "-");
        const url = `/${tenant}/assets/${encodedAsset}/${encodedField}/${encodedWell}/samplevalidation/${sampleId}`;
        this.$router.push(url);
        localStorage.setItem("lastView", url);
      } else {
        const notAvailable = "NotAvail";
        const encodedAsset = asset ? encodeURIComponent(asset).replace(/'/g, "%27").replace(/ /g, "-") : notAvailable;
        const encodedField = field ? encodeURIComponent(field).replace(/'/g, "%27").replace(/ /g, "-") : notAvailable;
        const encodedWell = well ? encodeURIComponent(well).replace(/'/g, "%27").replace(/ /g, "-") : notAvailable;
        const url = `/${tenant}/assets/${encodedAsset}/${encodedField}/${encodedWell}/samplevalidation/${sampleId}`;
        this.$router.push(url);
        localStorage.setItem("lastView", url);
      }
    },
    navigateToSampleInformation(sampleId) {
      const tenant = getTenantPrefix();
      if (tenant && sampleId) {
        const currentItem = this.serverItems.find(item => item.ID === sampleId);
        if (currentItem && currentItem.location) {
          const asset = currentItem.location.asset ? encodeURIComponent(currentItem.location.asset).replace(/'/g, "%27").replace(/ /g, "-") : "NotAvail";
          const field = currentItem.location.field ? encodeURIComponent(currentItem.location.field).replace(/'/g, "%27").replace(/ /g, "-") : "NotAvail";
          const well = currentItem.location.well ? encodeURIComponent(currentItem.location.well).replace(/'/g, "%27").replace(/ /g, "-") : "NotAvail";
          const url = `/${tenant}/assets/${asset}/${field}/${well}/sampleinformation/${sampleId}`;
          this.$router.push(url);
          localStorage.setItem("lastView", url);
        } else {
          const url = `/${tenant}/assets/NotAvail/NotAvail/NotAvail/sampleinformation/${sampleId}`;
          this.$router.push(url);
          localStorage.setItem("lastView", url);
        }
      }
    },
    showSearchDropdown(column) {
      this.$nextTick(() => {
        if (this.searchDropdowns[column]) {
          this.fetchLocations(column);
        }
      });
    },
    async fetchLocations(columnName) {
      this.searchLoading = true;
      var columns = "";
      const query = {
        asset: this.selectedLocations.assets ? JSON.stringify(this.selectedLocations.assets) : [], // Convert to JSON string
        field: this.selectedLocations.fields ? JSON.stringify(this.selectedLocations.fields) : [], // Convert to JSON string
        well: this.selectedLocations.wells ? JSON.stringify(this.selectedLocations.wells) : [],   // Convert to JSON string
        reservoir: this.selectedLocations.reservoirs ? JSON.stringify(this.selectedLocations.reservoirs) : [], // Convert to JSON string
        fluidSampleID: this.selectedLocations.fluidSampleID ? JSON.stringify(this.selectedLocations.fluidSampleID) : [], // Convert to JSON string
      };

      if (columnName === "assets") {
        columns = "asset";
        query.asset = "[]"; // Reset asset filters
        query.field = "[]";
        query.well = "[]";
        query.reservoir = "[]";
        query.fluidSampleID = "[]";
      } else if (columnName === "fields") {
        columns = "field";
        query.field = "[]"; // Reset field filters
        query.well = "[]";
        query.reservoir = "[]";
        query.fluidSampleID = "[]";
      } else if (columnName === "reservoirs") {
        columns = "reservoir";
        query.reservoir = "[]"; // Reset reservoir filters
        query.fluidSampleID = "[]";
      } else if (columnName === "wells") {
        columns = "well";
        query.well = "[]"; // Reset well filters
        query.fluidSampleID = "[]";
      } else if (columnName === "fluidType") {
        columns = "fluidType";
        query.fluidSampleID = "[]"; // Reset sampleKind filters
      } else if (columnName === "tests") {
        columns = "tests";
        query.fluidSampleID = "[]"; // Reset tests filters
      } else if (columnName === "fluidSampleID") {
        columns = "fluidSampleID";
        query.fluidSampleID = "[]"; // Reset fluidSampleID filters
      }

      try {
        let response = "";
        let locations = "";
        if (columnName === "fluidType") {
          columns = "fluidType";
          response = await api.get(`/public/api/summary/${columnName}`, { params: query });
          if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
            locations = response.data.data.columnSummary.filter((fluidType) => fluidType.name) || [];
            this.searchDropdownItems[columnName] = locations.sort((a, b) => a.name ? a.name.localeCompare(b.name) : 0);
          }
          else if (response && response.data && response.data.message) {
            console.error(response.data.message);
          }
          else {
            console.error("Unexpected status code for fluidType");
          }
        } else if (columnName === "tests") {
          response = await api.get(`/public/api/list/${columnName}`);
          if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
            locations = response.data.data.list || [];
            this.searchDropdownItems[columnName] = locations;
          }
          else if (response && response.data && response.data.message) {
            console.error(response.data.message);
          }
          else {
            console.error("Unexpected status code for tests");
          }
        } else if (columnName === "fluidSampleID") {
          response = await api.get(`/public/api/unique-values/${columnName}`);
          if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
            locations = response.data.data.columnUniqueValues.Values.map(value => ({ name: value })) || [];
            this.searchDropdownItems[columnName] = locations;
          }
          else if (response && response.data && response.data.message) {
            console.error(response.data.message);
          }
          else {
            console.error("Unexpected status code for fluidSampleID");
          }
        } else {
          response = await api.get(`/public/api/locations/${columnName}`, { params: query });
          if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
            locations = response.data.data.locations || [];
            this.searchDropdownItems[columnName] = locations.sort((a, b) => a.name.localeCompare(b.name));
          }
          else if (response && response.data && response.data.message) {
            console.error(response.data.message);
          }
          else {
            console.error("Unexpected status code for locations");
          }
        }
        if (this.filterDetails) {
          const filterValues = this.filterDetails[columns];
          if (Array.isArray(filterValues) && filterValues.length > 0) {
            this.searchDropdownItems[columnName].forEach((item) => {
              item.isSelected = filterValues.includes(item.name);
            });
          } else {
            this.searchDropdownItems[columnName].forEach((item) => {
              item.isSelected = false;
            });
          }
        }
      } catch (error) {
        console.error(`Error fetching locations for ${columnName}:`, error);
        this.searchDropdownItems[columnName] = [];
      } finally {
        this.searchLoading = false;
      }
    },

    selectLocation() {
      const columnMapping = {
        assets: "asset",
        fields: "field",
        wells: "well",
        reservoirs: "reservoir",
        fluidType: "fluidType",
        tests: "selectedTests",
        fluidSampleID: "fluidSampleID", // Added fluidSampleID mapping
      };

      if (this.filterDetails?.ID) {
        for (const [key, locations] of Object.entries(this.selectedLocations)) {
          const filterKey = columnMapping[key];
          if (filterKey && Array.isArray(this.filterDetails[filterKey])) {
            locations.forEach((location) => {
              if (!this.filterDetails[filterKey].includes(location)) {
                this.filterDetails[filterKey].push(location);
              }
            });
          }
        }
        const withFilter = true;
        this.$emit("getFilterFromTable", {
          ...this.filterDetails,
          withFilter,
          page: this.page,
          itemsPerPage: this.itemsPerPage,
        });
      }

      else {

        this.$emit("getFilterFromTable", {
          ...this.selectedLocations,
          page: this.page,
          itemsPerPage: this.itemsPerPage,
        });
      }
      this.loadItems();
    },
    resetTableFilter() {
      this.searchDropdownItems = {
        assets: [],
        fields: [],
        wells: [],
        reservoirs: [],
        fluidType: [],
        tests: [],
        fluidSampleID: []
      };
      this.selectedLocations = {
        assets: [],
        fields: [],
        wells: [],
        reservoirs: [],
        fluidTypes: [],
        tests: [],
        fluidSampleID: []
      };
      this.serverItems = [];
      this.totalItems = 0;
      this.loading = true;
      this.$emit('update:pagination', { page: 1, itemsPerPage: Number(this.itemsPerPage) });
    },
    sort(sortColumn, order) {
      const columnMapping = {
        assets: "asset",
        fields: "field",
        wells: "well",
        reservoirs: "reservoir",
        fluidType: "fluidType",
        tests: "selectedTests",
        fluidSampleID: "fluidSampleID"
      };

      if (sortColumn.toLowerCase() === 'sample id') {
        sortColumn = 'fluidSampleID';
      } else {
        sortColumn = sortColumn.toLowerCase();
      }

      if (this.filterDetails) {
        for (const [key, locations] of Object.entries(this.selectedLocations)) {
          const filterKey = columnMapping[key];
          if (filterKey && Array.isArray(this.filterDetails[filterKey])) {
            locations.forEach((location) => {
              if (!this.filterDetails[filterKey].includes(location)) {
                this.filterDetails[filterKey].push(location);
              }
            });
          }
        }
        const withFilter = true;
        this.$emit("getFilterFromTable", {
          ...this.filterDetails,
          sortColumn, // Column to sort by
          sortDirection: order, // asc or desc
          withFilter,
          page: this.page,
          itemsPerPage: this.itemsPerPage,
        });
      }

      else {
        this.$emit("getFilterFromTable", {
          ...this.selectedLocations,
          sortColumn, // Column to sort by
          sortDirection: order, // asc or desc
          page: this.page,
          itemsPerPage: this.itemsPerPage,
        });
      }
      this.loadItems();
    },
    async downloadTableCSV() {
      this.loadingCsv = true;
        await this.fetchSamples();
      
      const headers = ['Sample ID', 'Asset', 'Field', 'Reservoir', 'Well', 'Fluid Type', 'Tests'];

      const rows = this.samplesCSV.map((item) => {
        const tests = [];

        if (item.compositiontests) tests.push(item.compositiontests.name || 'Composition');
        if (item.ccetests?.length) tests.push(item.ccetests[0].name || 'CCE Test');
        if (item.dltests?.length) tests.push(item.dltests[0].name || 'DL Test');
        if (item.cvdtests?.length) tests.push(item.cvdtests[0].name || 'CVD Test');
        if (item.separatortests?.length) tests.push(item.separatortests[0].name || 'Separator Test');
        if (item.viscositytests?.length) tests.push(item.viscositytests[0].name || 'Viscosity Test');

        return [
          item.fluidSampleID || '',
          item.location.asset || '',
          item.location.field || '',
          item.location.reservoir || '',
          item.location.well || '',
          item.fluidType || '',
          tests.join(', '), // Combine tests into a single string
        ];
      });

      const csvContent = [
        headers.join(','), // Convert headers to CSV format
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')), // Escape cells and join rows
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'Fluids_Characterization_Data.csv';
      link.click();
      URL.revokeObjectURL(link.href);
      this.loadingCsv = false;
    },
    async fetchSamples() {
      try {
        const params = {
          pageSize: this.totalItems,
          pageNumber: 1,
          viewSummary: true
        };
        if (this.filterDetails) {
          params.field = this.filterDetails.field?.length ? `["${this.filterDetails.field.join('","')}"]` : '[]';
          params.well = this.filterDetails.well?.length ? `["${this.filterDetails.well.join('","')}"]` : '[]';
          params.asset = this.filterDetails.asset?.length ? `["${this.filterDetails.asset.join('","')}"]` : '[]';
          params.reservoir = this.filterDetails.reservoir?.length ? `["${this.filterDetails.reservoir.join('","')}"]` : '[]';
          params.fluidType = this.filterDetails.fluidType?.length ? `["${this.filterDetails.fluidType.join('","')}"]` : '[]';
                    params.sampleType = this.filterDetails.sampleType?.length ? `["${this.filterDetails.sampleType.join('","')}"]` : '[]';
              params.fluidClassification = this.filterDetails.fluidClassification?.length ? `["${this.filterDetails.fluidClassification.join('","')}"]` : '[]';
          params.selectedTests = this.filterDetails.selectedTests?.length
            ? `["${this.filterDetails.selectedTests.map(test => test.toLowerCase().replace(/\s+/g, '')).join('","')}"]`
            : '[]';
          params.fluidSampleID = this.filterDetails.fluidSampleID?.length ? `["${this.filterDetails.fluidSampleID.join('","')}"]` : '[]';
        }
        const response = await api.get('public/api/samples', { params });
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          this.samplesCSV = response.data.data.samples; // Assuming the API response has this structure
        }
        else if (response && response.data && response.data.message) {
          console.error(response.data.message);
        }
        else {
          console.error("Error fetching samples");
        }
      } catch (error) {
        console.error('Error fetching samples:', error);
      }
    },
    async downloadPvtInput(item) {
      this.snackbarMessage = null;
      this.sourceDownloadId = item.ID;
      try {
        const sampleId = this.getPvtSampleId(item);
        const result = await executePvtInputDownload({
          httpGet: () =>
            api.get(`public/api/pvt-sample/${sampleId}/download/input`, {
              responseType: "blob",
            }),
        });
        if (!result.ok && !result.limitModal && result.snackbarMessage) {
          this.snackbarMessage = result.snackbarMessage;
        }
      } finally {
        this.sourceDownloadId = null;
      }
    },
    async goToPdf(sampleId, extension) {
      this.snackbarMessage = null;
      this.sourceDownloadId = sampleId;
      try {
        const result = await executeSampleReportDownload({
          extension,
          httpGet: () =>
            api.get(process.env.VUE_APP_API_URL + `public/api/samples/${sampleId}/download`, {
              responseType: 'blob',
            }),
        });
        if (!result.ok && !result.limitModal && result.snackbarMessage) {
          this.snackbarMessage = result.snackbarMessage;
        }
      } finally {
        this.sourceDownloadId = null;
      }
    },
    async exportToPVTSim(sampleID, fluidSampleID) {
      this.exportDownloadId = sampleID;
      try {
        const response = await api.post(`public/api/samples/export/${sampleID}`, {});
        
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          const payload = response.data.data || response.data || {};
          const filename = payload.filename || `Calsep-file-${fluidSampleID}.xlsx`;
          const contentType =
            payload.contentType ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
          const base64Content = payload.content;

          try {
            if (typeof base64Content === "string" && base64Content.trim()) {
              let value = base64Content.trim();
              if (value.startsWith("data:") && value.includes(",")) {
                value = value.split(",", 2)[1];
              }
              const binary = atob(value);
              const bytes = new Uint8Array(binary.length);
              for (let i = 0; i < binary.length; i += 1) {
                bytes[i] = binary.charCodeAt(i);
              }
              const blob = new Blob([bytes], { type: contentType });
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = filename;
              link.rel = "noopener";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            } else if (payload.downloadUrl) {
              throw new Error("Export content missing from response");
            } else {
              throw new Error("Export file not found in response");
            }
          } catch (downloadError) {
            console.error("PVTSim file download failed:", downloadError);
            store.commit("api/showSnackbar", {
              message: "Failed to download the PVTSim file. Please try again.",
              color: "#E53535",
              timeout: 5000,
            });
            return;
          }
          
          store.commit('api/showSnackbar', {
            message: response.data.data?.message || 'Export successful',
            color: '#4CAF50',
            timeout: 3000
          });
        } else {
          const errorMessage = response.data?.message || response.data?.data?.message || 'Error exporting to PVTSim';
          store.commit('api/showSnackbar', {
            message: errorMessage,
            color: '#E53535',
            timeout: 5000
          });
        }
      } catch (error) {
        console.error('Error exporting to PVTSim:', error);
        let errorMessage = 'Error exporting to PVTSim. Please try again.';
        
        if (error.response) {
          if (error.response.data) {
            if (typeof error.response.data === 'object') {
              errorMessage = error.response.data.message || 
                           error.response.data.data?.message || 
                           error.response.data.error || 
                           errorMessage;
            } else if (error.response.data instanceof Blob) {
              try {
                const text = await new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result);
                  reader.onerror = reject;
                  reader.readAsText(error.response.data);
                });
                const errorData = JSON.parse(text);
                errorMessage = errorData.message || errorData.data?.message || errorData.error || errorMessage;
              } catch (e) {
                void 0;
              }
            }
          }
          
          if (error.response.status) {
            errorMessage = `Error ${error.response.status}: ${errorMessage}`;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        store.commit('api/showSnackbar', {
          message: errorMessage,
          color: '#E53535',
          timeout: 5000
        });
      } finally {
        this.exportDownloadId = null;
      }
    },
    
    populateSelectedLocations(filterDetails) {
      const columnMapping = {
        asset: "assets",
        field: "fields", 
        well: "wells",
        reservoir: "reservoirs",
        fluidType: "fluidType",
        selectedTests: "tests",
        fluidSampleID: "fluidSampleID"
      };
      
      for (const [filterKey, locations] of Object.entries(filterDetails)) {
        const columnKey = columnMapping[filterKey];
        if (columnKey && Array.isArray(locations) && locations.length > 0) {
          this.selectedLocations[columnKey] = [...locations];
        }
      }
    }
  },
  watch: {
    filterDetails: {
      handler(newVal) {
        if (newVal) {
          this.populateSelectedLocations(newVal);
        }
      },
      immediate: true
    },
    filterData: {
      handler() {
        this.loadItems();
      },
      immediate: true,
      deep: true
    },
    isLoading: {
      handler(newVal) {
        this.loading = newVal;
        if (!newVal) {
          this.loadItems();
        }
      },
      immediate: true
    }
  }
}
</script>
